import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { useEffect, useRef } from "react";
import { NavLink, Outlet, useMatch } from "react-router-dom";

import { get } from "api";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import AdonisAiIcon from "modules/adonis/assets/ai-icon.svg";
import { useUserPreferences } from "modules/user/useUser/useUser";
import TopNav from "molecules/navbars/TopNav";
import { useFeatureFlag } from "services/Flags";
import { checkIfListingsHaveConcerns } from "services/HelperService";
import { getCustomCategoryDestinationUrl } from "toolbox/CustomCategories";

type NavButtonProps = {
  exact?: boolean;
  to: string;
  children: React.ReactNode;
};
const NavButton = ({ exact, to, children }: NavButtonProps) => {
  const matches = useMatch(to);
  const itemRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (itemRef.current && matches) {
      itemRef.current?.scrollIntoView({
        behavior: "smooth",
        inline: "start",
        block: "nearest",
      });
    }
  }, [matches]);
  return (
    <NavLink
      to={to}
      end={exact}
      className={(p) =>
        cx(
          "scroll-ml-[20px] pb-[10px]",
          p.isActive && "border-b-2 border-brand-color text-brand-color"
        )
      }
      ref={itemRef}
    >
      {children}
    </NavLink>
  );
};

const ShopNav = () => {
  const retailProductsFeatureFlag = useFeatureFlag("selling-products");
  const giftCardsFeatureFlag = useFeatureFlag("giftcards");
  const { isPersonalisationEnabled } = useAdonisChat();
  const { preferences } = useUserPreferences();

  const { data: platform } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
  });

  const { data: areRetailProductsVisible } = useQuery({
    queryKey: ["/retailproducts/are-retail-products-visible"],
    queryFn: () => get("/retailproducts/are-retail-products-visible"),
    staleTime: 1000 * 10,
  });

  const isRetailProductsEnabled =
    retailProductsFeatureFlag &&
    platform?.platformSettings?.sellingProductsEnabled &&
    !!areRetailProductsVisible;

  const isGiftCardsEnabled =
    giftCardsFeatureFlag && platform?.platformSettings?.giftCardEnabled;

  const { data: listings, isFetched: isListingsFetched } = useQuery({
    queryKey: ["/listings"],
    queryFn: () => get("/listings"),
    staleTime: 1000 * 60,
  });
  const { data: memberships, isFetched: isMembershipsFetched } = useQuery({
    queryKey: ["/memberships"],
    queryFn: () => get("/memberships"),
  });
  const { data: shopData, isFetched: isShopDataFetched } = useQuery({
    queryKey: ["/shop"],
    queryFn: () => get("/shop"),
  });
  const { data: customShopCategories } = useQuery({
    queryKey: ["/shop/custom-categories/listings"],
    queryFn: () => get("/shop/custom-categories/listings"),
    staleTime: 1000 * 10,
  });
  const { data: giftCards, isFetched: isGiftCardsFetched } = useQuery({
    queryKey: ["/gift-cards"],
    queryFn: () => get("/gift-cards", {}),
    enabled: isGiftCardsEnabled,
  });

  const { data: retailProducts, isFetched: isRetailProductsFetched } = useQuery(
    ["/retailproducts/{platformId}/product-listings", platform?.id],
    async () => {
      if (!platform?.id) return [];
      return await get("/retailproducts/{platformId}/product-listings", {
        platformId: platform.id,
      });
    },
    {
      enabled: !!(isRetailProductsEnabled && platform?.id),
    }
  );

  const availableCustomShopCategories =
    customShopCategories?.filter((csc) => csc.categoryInclusions.length > 0) ??
    [];

  const treatments = listings?.filter((listing) => listing.type == "Treatment");
  const packages = listings?.filter((listing) => listing.type == "Package");
  const hasCustomShopCategoriesWithGiftCards =
    isGiftCardsEnabled &&
    availableCustomShopCategories.some((csc) =>
      csc.categoryInclusions.some(
        (ci) =>
          ci.packageListingId &&
          giftCards?.some((gc) => gc.id === ci.packageListingId)
      )
    );

  const showJustForYouLink =
    isPersonalisationEnabled &&
    preferences?.concernIds &&
    preferences.concernIds.length > 0;

  return (
    <div className="flex flex-row gap-4 overflow-x-auto whitespace-nowrap px-6 pt-[4px] text-bold2 text-secondary">
      <NavButton exact to="/shop">
        Browse
      </NavButton>
      {showJustForYouLink && (
        <NavButton to="/shop/recommended-treatments">
          <div className="flex items-center gap-2">
            <AdonisAiIcon className="size-4" />
            <span>Just for you</span>
          </div>
        </NavButton>
      )}
      {availableCustomShopCategories.map((customShopCategory) => (
        <NavButton
          key={customShopCategory.category?.id}
          to={getCustomCategoryDestinationUrl(
            customShopCategory,
            hasCustomShopCategoriesWithGiftCards
          )}
        >
          {`${customShopCategory.category.subTitle}`}
        </NavButton>
      ))}
      {(!isMembershipsFetched || (memberships && memberships.length > 0)) && (
        <NavButton to="/shop/memberships">Memberships</NavButton>
      )}
      {(!isListingsFetched || (treatments && treatments.length > 0)) && (
        <NavButton to="/shop/treatments">Treatments</NavButton>
      )}
      {isRetailProductsEnabled &&
        (!isRetailProductsFetched ||
          (retailProducts && retailProducts.length > 0)) && (
          <NavButton to="/shop/products">Products</NavButton>
        )}
      {(!isListingsFetched || (packages && packages.length > 0)) && (
        <NavButton to="/shop/packages">Packages</NavButton>
      )}
      {isGiftCardsEnabled &&
        (!isGiftCardsFetched || (giftCards && !!giftCards.length)) && (
          <NavButton to="/shop/gift-cards">Gift Cards</NavButton>
        )}
      {(!isShopDataFetched ||
        (shopData?.concerns &&
          shopData.concerns.length > 0 &&
          checkIfListingsHaveConcerns(listings))) && (
        <NavButton to="/shop/concerns">Browse by Concern</NavButton>
      )}
    </div>
  );
};

const ShopPage = () => {
  return (
    <>
      <TopNav footer={<ShopNav />}>
        <h1 className="text-xl">Shop</h1>
      </TopNav>
      <Outlet />
    </>
  );
};

export default ShopPage;
